/**
 * Parent-facing waitlist for a capped program (BHCS-124).
 *
 * Two modes, chosen by the program page:
 *   - `full`: no seat for this family. The enroll button is replaced by the full notice, each
 *     child already in line, and a way to put another child in line.
 *   - `open`: the family can enroll (a seat is free, or one is held for their child). Only their
 *     entries show: a held seat as a prompt to enroll, a waiting child with a way to leave.
 *
 * The school decides who is offered a seat, so nothing here promises an order of promotion; it
 * shows the child's place and leaves the rest to the offer email.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useLabels } from '../config/useLabels';
import { trackEvent } from '../utils/analytics';
import {
  joinWaitlist,
  withdrawFromWaitlist,
  type MyWaitlistEntry,
} from '../services/programWaitlist';
import type { ProgramSeats } from '../hooks/useProgramSeats';

interface WaitlistChild {
  id: string;
  first_name: string;
  last_name: string;
}

interface Props {
  programId: string;
  programName: string;
  mode: 'full' | 'open';
  seats: ProgramSeats;
  /** Every child of the signed-in parent, for naming entries. */
  allChildren: WaitlistChild[];
  /** Children who could join: active and not enrolled in this program. */
  eligibleChildren: WaitlistChild[];
}

function childName(children: WaitlistChild[], id: string): string {
  const child = children.find((c) => c.id === id);
  return child ? `${child.first_name} ${child.last_name}`.trim() : 'Your child';
}

export function ProgramWaitlist({ programId, programName, mode, seats, allChildren, eligibleChildren }: Props) {
  const t = useLabels();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [pickedChildId, setPickedChildId] = useState<string>('');

  const inLine = new Set(seats.myEntries.map((e) => e.student_id));
  const joinable = eligibleChildren.filter((c) => !inLine.has(c.id));
  const joinChildId = joinable.length === 1 ? joinable[0].id : pickedChildId;

  const track = (eventType: string, entryId: string, studentId: string) => {
    trackEvent({
      eventType,
      user,
      entityType: 'program_waitlist',
      entityId: entryId,
      metadata: { program_id: programId, program_name: programName, student_id: studentId },
      email: user?.email,
    });
  };

  const join = useMutation({
    mutationFn: (studentId: string) => joinWaitlist(programId, studentId),
    onSuccess: (result, studentId) => {
      setError(null);
      setPickedChildId('');
      track('waitlist_join', result.id, studentId);
      seats.refresh();
    },
    // A seat may have opened since the page loaded; the refresh flips the page back to Enroll.
    onError: (err: Error) => {
      setError(err.message);
      seats.refresh();
    },
  });

  const leave = useMutation({
    mutationFn: (entry: MyWaitlistEntry) => withdrawFromWaitlist(entry.id),
    onSuccess: (_result, entry) => {
      setError(null);
      track('waitlist_leave', entry.id, entry.student_id);
      seats.refresh();
    },
    onError: (err: Error) => setError(err.message),
  });

  const held = seats.myEntries.filter((e) => e.status === 'offered');
  const waiting = seats.myEntries.filter((e) => e.status === 'waiting');
  // A seat held for one child does not free one for a sibling, who can still get in line.
  const showJoin = seats.isFull && joinable.length > 0;

  if (mode === 'open' && seats.myEntries.length === 0) return null;

  return (
    <div className="mt-8 space-y-3" data-testid="program-waitlist">
      {mode === 'full' && (
        <div className="p-4 bg-amber-50 border-l-4 border-amber-400 rounded-md">
          <p className="text-sm sm:text-base text-amber-900">{t('program.fullNotice')}</p>
        </div>
      )}

      {held.map((entry) => (
        <div key={entry.id} className="p-4 bg-green-50 border-l-4 border-green-500 rounded-md">
          <p className="text-sm sm:text-base text-green-900">
            <span className="font-semibold">{childName(allChildren, entry.student_id)}:</span>{' '}
            {t('waitlist.seatHeld')}
          </p>
        </div>
      ))}

      {waiting.length > 0 && (
        <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
          {waiting.map((entry) => (
            <li key={entry.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
              <span className="text-sm text-gray-800">
                <span className="font-medium">{childName(allChildren, entry.student_id)}</span>
                {' · '}
                {t('waitlist.onWaitlist')}
                {entry.queue_position != null && <span className="text-gray-500"> · #{entry.queue_position}</span>}
              </span>
              <button
                type="button"
                className="text-sm font-medium text-gray-600 underline hover:text-gray-900 disabled:opacity-50"
                onClick={() => leave.mutate(entry)}
                disabled={leave.isPending}
              >
                {t('action.leaveWaitlist')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {showJoin && (
        <div className="flex flex-wrap items-end justify-end gap-2">
          {joinable.length > 1 && (
            <label className="flex flex-col text-xs sm:text-sm text-gray-700">
              {t('waitlist.whichChild')}
              <select
                className="mt-1 border rounded px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                value={pickedChildId}
                onChange={(e) => setPickedChildId(e.target.value)}
              >
                <option value="">--</option>
                {joinable.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.first_name} {child.last_name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            type="button"
            className="brand-cta px-4 py-2 text-white rounded-[var(--brand-btn-radius)] transition-colors disabled:opacity-50"
            onClick={() => joinChildId && join.mutate(joinChildId)}
            disabled={!joinChildId || join.isPending}
          >
            {t('action.joinWaitlist')}
          </button>
        </div>
      )}

      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
    </div>
  );
}
